import { useState, useCallback } from 'react';
import { supabase } from '@/lib/supabase/client';
import { SubContract, DeliveryChallan, SubcontractReceipt } from '@/types/subcontracting';

/**
 * Subcontracting data for a specific operation
 * Note: The sub_contract_lines.routing_operation_id column actually references
 * work_order_operations.id (not routing_operations.id) due to a naming issue in the schema.
 * We use work_order_operation_id terminology in this hook for clarity.
 */
export interface OperationSubcontractingData {
  work_order_operation_id: string;
  sub_contract?: SubContract;
  delivery_challan?: DeliveryChallan;
  receipt?: SubcontractReceipt;
}

/**
 * Hook to fetch subcontracting data for work order operations
 * This includes subcontracts, delivery challans, and receipts
 */
export const useOperationSubcontracting = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Fetch subcontracting data for multiple work order operations
   * @param work_order_operation_ids Array of work order operation IDs
   * @returns Map of work_order_operation_id to subcontracting data
   *
   * Note: The DB column is named routing_operation_id but it actually references
   * work_order_operations.id due to a schema naming issue.
   */
  const fetchOperationSubcontracts = useCallback(async (
    work_order_operation_ids: string[]
  ): Promise<Map<string, OperationSubcontractingData>> => {
    if (!work_order_operation_ids || work_order_operation_ids.length === 0) {
      return new Map();
    }

    setLoading(true);
    setError(null);

    try {
      // Fetch subcontracts with lines that match the work order operations
      // Note: The column is named routing_operation_id but references work_order_operations.id
      const { data: subcontracts, error: scError } = await supabase
        .from('sub_contracts')
        .select(`
          *,
          lines:sub_contract_lines!inner(
            *,
            routing_operation_id
          )
        `)
        .in('lines.routing_operation_id', work_order_operation_ids)
        .order('created_at', { ascending: false });

      if (scError) throw scError;

      // Fetch delivery challans for these subcontracts
      const subcontract_ids = subcontracts?.map(sc => sc.id) || [];
      let delivery_challans: any[] = [];

      if (subcontract_ids.length > 0) {
        const { data: dcs, error: dcError } = await supabase
          .from('delivery_challans_with_details')
          .select('*')
          .in('sub_contract_id', subcontract_ids)
          .order('created_at', { ascending: false });

        if (dcError) throw dcError;
        delivery_challans = dcs || [];
      }

      // Fetch receipts for these subcontracts
      let receipts: any[] = [];

      if (subcontract_ids.length > 0) {
        const { data: rcpts, error: rcptError } = await supabase
          .from('subcontract_receipts_with_details')
          .select('*')
          .in('sub_contract_id', subcontract_ids)
          .order('created_at', { ascending: false });

        if (rcptError) throw rcptError;
        receipts = rcpts || [];
      }

      // Build map of work_order_operation_id to subcontracting data
      const result = new Map<string, OperationSubcontractingData>();

      subcontracts?.forEach((sc: any) => {
        // Each subcontract can have multiple lines for different operations
        sc.lines?.forEach((line: any) => {
          // routing_operation_id in the line is actually work_order_operation_id
          if (work_order_operation_ids.includes(line.routing_operation_id)) {
            // Find the latest DC for this subcontract
            const dc = delivery_challans.find(d => d.sub_contract_id === sc.id);

            // Find the latest receipt for this subcontract
            const receipt = receipts.find(r => r.sub_contract_id === sc.id);

            result.set(line.routing_operation_id, {
              work_order_operation_id: line.routing_operation_id,
              sub_contract: sc,
              delivery_challan: dc,
              receipt: receipt,
            });
          }
        });
      });

      return result;
    } catch (err: any) {
      console.error('Error fetching operation subcontracts:', err);
      setError(err.message);
      return new Map();
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Fetch subcontracting data for a single work order operation
   */
  const fetchOperationSubcontract = useCallback(async (
    work_order_operation_id: string
  ): Promise<OperationSubcontractingData | null> => {
    const result = await fetchOperationSubcontracts([work_order_operation_id]);
    return result.get(work_order_operation_id) || null;
  }, [fetchOperationSubcontracts]);

  return {
    loading,
    error,
    fetchOperationSubcontracts,
    fetchOperationSubcontract,
  };
};

